import React, { useEffect, useState, useRef } from "react";
import {
  Container,
  TextField,
  Button,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper, FormControl, InputLabel, Select, InputAdornment, MenuItem,
  IconButton,
  Modal,
  Backdrop,
  Fade,
  AppBar,
  Toolbar,
  Typography,
  Box
} from "@mui/material";
import { Edit, Delete, Visibility, Print, NavigateBefore, NavigateNext, CloudDownload } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import IdCard from '@mui/icons-material/Badge';
import jsPDF from "jspdf";
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import CloseIcon from '@mui/icons-material/Close';
import { useParams } from "react-router-dom";
import base64 from "base64-js";
import LocationOnIcon from "@mui/icons-material/LocationOn";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

// Enhanced PDF Image Manager with improved sequencing and error handling
class ImprovedPDFImageManager {
  constructor() {
    this.imageCache = new Map();
    this.maxCacheSize = 50;
  }

  // ✅ NEW: Fetch school details from same API used in admission form
  async fetchSchoolDetails() {
    try {
      const token = sessionStorage.getItem("token");
      if (!token) throw new Error("No auth token found");

      const response = await fetch('https://wsmsv1.aidigitalsec.com/api/FeeReport/GetSchoolName', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token,
        },
        body: JSON.stringify({}),
      });

      if (!response.ok) throw new Error(`API error: ${response.status}`);

      const apiData = await response.json();
      const school = apiData[0];

      // Process logo from API response
      let logoDataUrl = null;
      const logoImg = school?.logoImg;
      if (logoImg && typeof logoImg === 'string' && logoImg.trim() !== '') {
        try {
          let cleanedBase64 = logoImg
            .replace(/^data:image\/[a-zA-Z]+;base64,/, '')
            .replace(/\s/g, '')
            .replace(/\n/g, '');
          const paddingLength = (4 - (cleanedBase64.length % 4)) % 4;
          cleanedBase64 += '='.repeat(paddingLength);
          if (/^[A-Za-z0-9+/=]+$/.test(cleanedBase64)) {
            logoDataUrl = `data:image/png;base64,${cleanedBase64}`;
          }
        } catch (e) {
          console.warn("⚠️ Logo processing failed:", e.message);
        }
      }

      return {
        schoolName: school?.schoolName?.replace(/['"]+/g, '') || 'N/A',
        address: school?.address?.replace(/['"]+/g, '') || '',
        phoneNo: school?.phoneNo?.replace(/['"]+/g, '') || '',
        logoDataUrl,
      };
    } catch (error) {
      console.error("❌ Failed to fetch school details:", error.message);
      // Fallback to sessionStorage if API fails
      return {
        schoolName: sessionStorage.getItem("organizationName")?.replace(/['"]+/g, '') || 'N/A',
        address: sessionStorage.getItem("address")?.replace(/['"]+/g, '') || '',
        phoneNo: sessionStorage.getItem("phoneNo")?.replace(/['"]+/g, '') || '',
        logoDataUrl: null,
      };
    }
  }

  // ✅ UPDATED: Main PDF generation — fetches school details from API first
  async generateIdCardPDF(studentData) {
    try {
      console.log("🔄 Starting ID Card PDF generation...");
      this.showLoadingIndicator();

      // Step 1: Fetch school details from API
      console.log("🏫 Step 1: Fetching school details from API...");
      const schoolDetails = await this.fetchSchoolDetails();
      console.log("✅ School details fetched:", schoolDetails.schoolName);

      // Step 2: Load and validate all images BEFORE building PDF
      console.log("📸 Step 2: Loading all images...");
      const [studentImageResult] = await Promise.allSettled([
        this.handleStudentImage(studentData)
      ]);

      // Process student image result
      let validStudentImageResult = null;
      if (studentImageResult.status === 'fulfilled' && studentImageResult.value) {
        validStudentImageResult = studentImageResult.value;
        console.log("✅ Student image loaded successfully");
      } else {
        console.warn("⚠️ Student image loading failed, using default avatar");
        validStudentImageResult = this.createDefaultAvatar();
      }

      // Process logo from school details
      let validLogoResult = null;
      if (schoolDetails.logoDataUrl) {
        validLogoResult = { type: 'base64', data: schoolDetails.logoDataUrl };
        console.log("✅ Logo prepared from API");
      } else {
        console.warn("⚠️ No logo from API, trying URL-based logo...");
        const logoUrlResult = await this.handleLogoImage();
        if (logoUrlResult) validLogoResult = logoUrlResult;
      }

      // Step 3: Validate images before proceeding
      console.log("🔍 Step 3: Validating images...");
      const [logoValidated, studentImageValidated] = await Promise.all([
        validLogoResult ? this.validateImageData(validLogoResult) : Promise.resolve(null),
        this.validateImageData(validStudentImageResult)
      ]);

      if (!studentImageValidated) {
        console.warn("⚠️ Student image is not valid, cannot generate ID card.");
        this.hideLoadingIndicator();
        return;
      }

    // Step 4: Build PDF — landscape card matching new design
const doc = new jsPDF({
  orientation: "landscape",
  unit: "mm",
  format: [140, 95],
});

      // Step 5: Build PDF structure with school details from API
      console.log("🏗️ Step 5: Building PDF structure...");
      await this.buildPDFStructure(doc, studentData, schoolDetails, logoValidated);

      // Step 6: Add validated student image
      console.log("🖼️ Step 6: Adding student image...");
    await this.addValidatedImageToPDF(doc, studentImageValidated, 106, 29, 30, 42); 

      // Step 7: Add student content with school details from API
      console.log("📝 Step 7: Adding student content...");
      this.addStudentContent(doc, studentData, schoolDetails);

      // Step 8: Generate and open PDF
      console.log("📄 Step 8: Generating final PDF...");
      this.generateAndOpenPDF(doc);

      console.log("✅ ID Card PDF generated successfully");
      this.hideLoadingIndicator();

    } catch (error) {
      console.error("❌ PDF generation failed:", error);
      this.hideLoadingIndicator();
      this.showErrorMessage(`Failed to generate ID card: ${error.message}`);
    }
  }

  // New method to validate image data before using in PDF
  async validateImageData(imageResult) {
    if (!imageResult || !imageResult.data) {
      return null;
    }

    return new Promise((resolve) => {
      const testImg = new Image();
      const timeout = setTimeout(() => {
        console.warn("⚠️ Image validation timeout");
        resolve(null);
      }, 5000);

      testImg.onload = () => {
        clearTimeout(timeout);
        console.log("✅ Image validation successful");
        resolve(imageResult);
      };

      testImg.onerror = (error) => {
        clearTimeout(timeout);
        console.warn("⚠️ Image validation failed:", error);
        resolve(null);
      };

      testImg.src = imageResult.data;
    });
  }

  // ✅ UPDATED: Build PDF structure using school details from API
async buildPDFStructure(doc, studentData, schoolDetails, validatedLogoResult) {
  const pageWidth = 140, pageHeight = 95;
  const navy = [11, 31, 61];
  const orange = [237, 158, 15];
  const contentWidth = 100; // stop before photo column at x=104

  doc.setFillColor(255, 255, 255);
  doc.rect(0, 0, pageWidth, pageHeight, "F");

  if (validatedLogoResult) {
    try {
     await this.addValidatedImageToPDF(doc, validatedLogoResult, 5, 2, 27, 17); 
    } catch (error) {
      console.warn("⚠️ Failed to add logo to PDF:", error.message);
    }
  }

  let schoolName = schoolDetails.schoolName || "";
  if (!/trust/i.test(schoolName)) schoolName = `${schoolName} (Trust)`;

  doc.setFont("helvetica", "bold");
  let fontSize = 16;
  doc.setFontSize(fontSize);
  const maxTitleWidth = contentWidth + 10; // keep clear of logo + photo column
  while (doc.getTextWidth(schoolName) > maxTitleWidth && fontSize > 8) {
    fontSize -= 0.5;
    doc.setFontSize(fontSize);
  }
  doc.setTextColor(...navy);
  // Center within the content column only (0 to 100), not full page width
 doc.text(schoolName, 82, 8, { align: "center" });

  doc.setDrawColor(...orange);
  doc.setLineWidth(0.8);
doc.line(42, 10, 122, 10);

  const bannerText = `STUDENT IDENTITY CARD ${studentData.session?.trim() || "N/A"}`;
  doc.setFontSize(9.5);
  const bannerTextWidth = doc.getTextWidth(bannerText);
  const bannerPadX = 4;
 const bannerCenterX = 82;
const bannerW = bannerTextWidth + bannerPadX * 2;
const bannerX = bannerCenterX - bannerW / 2;
const bannerY = 11.5;      // was 19
const bannerH = 4.5;       // was 6.5

 doc.setFillColor(...orange);
doc.rect(bannerX, bannerY, bannerW, bannerH, "F");
doc.setTextColor(...navy);
doc.text(bannerText, bannerCenterX, bannerY + bannerH / 2 + 1.4, { align: "center" });

  // Photo box — same coords as image draw call above
  doc.setDrawColor(...navy);
  doc.setLineWidth(0.9);
  doc.rect(104, 27, 34, 46);
}

  // Enhanced image addition with better error handling
  async addValidatedImageToPDF(doc, validatedImageResult, x, y, width, height) {
    if (!validatedImageResult || !validatedImageResult.data) {
      console.warn("⚠️ No validated image data, adding placeholder");
      this.addPlaceholderToPDF(doc, x, y, width, height);
      return;
    }

    return new Promise((resolve) => {
      try {
        console.log("🔄 Adding validated image to PDF...");

        const formats = ['JPEG', 'PNG', 'GIF', 'WEBP'];
        let success = false;
        let lastError = null;

        for (const format of formats) {
          try {
            doc.addImage(validatedImageResult.data, format, x, y, width, height);
            console.log(`✅ Successfully added image as ${format}`);
            success = true;
            break;
          } catch (formatError) {
            console.warn(`⚠️ Failed to add image as ${format}:`, formatError.message);
            lastError = formatError;
          }
        }

        if (!success) {
          console.warn("⚠️ All image formats failed, adding placeholder");
          console.warn("Last error:", lastError?.message);
          this.addPlaceholderToPDF(doc, x, y, width, height);
        }

        resolve();
      } catch (error) {
        console.error("❌ Error in addValidatedImageToPDF:", error);
        this.addPlaceholderToPDF(doc, x, y, width, height);
        resolve();
      }
    });
  }

  // Logo handling with better debugging (fallback if API logo is absent)
  async handleLogoImage() {
    console.log("=== LOGO DEBUGGING START ===");

    const logoUrl = sessionStorage.getItem("logoImg");
    console.log("Raw logo URL from session:", logoUrl);

    if (!logoUrl || logoUrl.trim() === '') {
      console.log("❌ No logo URL found in sessionStorage");
      return null;
    }

    const cleanedUrl = logoUrl.replace(/['"]+/g, '').trim();
    console.log("Cleaned logo URL:", cleanedUrl);

    if (cleanedUrl.startsWith('data:image/')) {
      console.log("✅ Using direct base64 logo");
      return { type: 'base64', data: cleanedUrl };
    }

    if (this.isBase64String(cleanedUrl)) {
      console.log("✅ Formatting raw base64 logo");
      const formattedLogo = this.formatBase64ForPDF(cleanedUrl);
      return { type: 'base64', data: formattedLogo };
    }

    const cacheKey = `logo_${cleanedUrl}`;
    const cachedLogo = this.getCachedImage(cacheKey);
    if (cachedLogo) {
      console.log("✅ Using cached logo");
      return { type: 'base64', data: cachedLogo };
    }

    console.log("🔄 Attempting to load logo from URL...");
    const logoSources = this.getLogoSources(cleanedUrl);

    for (let i = 0; i < logoSources.length; i++) {
      const source = logoSources[i];
      console.log(`Trying source ${i + 1}/${logoSources.length}: ${source.name}`);
      try {
        const logoData = await this.loadImageFromSource(source);
        if (logoData) {
          console.log(`✅ Successfully loaded logo from ${source.name}`);
          this.cacheImage(cacheKey, logoData);
          return { type: 'base64', data: logoData };
        }
      } catch (error) {
        console.warn(`❌ Failed to load logo from ${source.name}:`, error.message);
      }
    }

    console.log("❌ All logo sources failed");
    console.log("=== LOGO DEBUGGING END ===");
    return null;
  }

  // Student image handling
  async handleStudentImage(studentData) {
  console.log("=== STUDENT IMAGE DEBUGGING START ===");
const imagePath = studentData.profileImage || studentData.studentImagePath || studentData.studentProfileLogo;
  console.log("Student image path:", imagePath);

  if (!imagePath || imagePath.trim() === '') {
    console.log("❌ No student image path provided, using default avatar");
    return this.createDefaultAvatar();
  }

  const cleanedPath = imagePath.replace(/['"]+/g, '').trim();
  console.log("Cleaned student image path:", cleanedPath);

  // ✅ Already base64
  if (cleanedPath.startsWith('data:image/')) {
    console.log("✅ Using direct base64 student image");
    return { type: 'base64', data: cleanedPath };
  }

  // ✅ Raw base64 string
  if (this.isBase64String(cleanedPath)) {
    console.log("✅ Formatting raw base64 student image");
    const formattedImage = this.formatBase64ForPDF(cleanedPath);
    return { type: 'base64', data: formattedImage };
  }

  // ✅ Check cache
  const cacheKey = `student_${cleanedPath}`;
  const cachedImage = this.getCachedImage(cacheKey);
  if (cachedImage) {
    console.log("✅ Using cached student image");
    return { type: 'base64', data: cachedImage };
  }

  // ✅ Build full URL
 const baseUrl = (process.env.REACT_APP_DOCUMENT_URL || 'https://wsmsv1.aidigitalsec.com').replace(/\/$/, '');

const fullUrl = cleanedPath.startsWith('http')
  ? cleanedPath
  : `${baseUrl}/${cleanedPath.replace(/^[~\/]+/, '')}`;

  console.log("Full student image URL:", fullUrl);

  // ✅ Strategy 1: HTML Image element + Canvas (bypasses CORS preflight — works on production)
  const tryImageElement = () => {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';

      const timeout = setTimeout(() => {
        console.warn("⏰ Image element timeout");
        img.src = '';
        resolve(null);
      }, 20000);   // was 8000

img.onerror = (e) => {
  clearTimeout(timeout);
  console.warn("⚠️ Image load failed:", fullUrl, e);
  resolve(null);
};

      img.onload = () => {
        clearTimeout(timeout);
        try {
          const canvas = document.createElement('canvas');
          canvas.width = img.naturalWidth || 200;
          canvas.height = img.naturalHeight || 200;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          const dataUrl = canvas.toDataURL('image/png');
          console.log("✅ Image loaded via HTML Image element + Canvas");
          resolve(dataUrl);
        } catch (canvasErr) {
          console.warn("⚠️ Canvas tainted (CORS blocked canvas):", canvasErr.message);
          resolve(null);
        }
      };

      img.onerror = () => {
        clearTimeout(timeout);
        console.warn("⚠️ HTML Image element failed");
        resolve(null);
      };

      img.src = fullUrl;
    });
  };

 

  // --- Run strategies in order ---

  // Strategy 1: HTML Image + Canvas
  const imageElementResult = await tryImageElement();
  if (imageElementResult) {
    this.cacheImage(cacheKey, imageElementResult);
    return { type: 'base64', data: imageElementResult };
  }

  // Strategy 2: allorigins proxy
  const allOriginsResult = await tryProxy(
    `https://api.allorigins.win/raw?url=${encodeURIComponent(fullUrl)}`,
    'allorigins'
  );
  if (allOriginsResult) {
    this.cacheImage(cacheKey, allOriginsResult);
    return { type: 'base64', data: allOriginsResult };
  }

  // Strategy 3: corsproxy.io
  const corsProxyResult = await tryProxy(
    `https://corsproxy.io/?${encodeURIComponent(fullUrl)}`,
    'corsproxy.io'
  );
  if (corsProxyResult) {
    this.cacheImage(cacheKey, corsProxyResult);
    return { type: 'base64', data: corsProxyResult };
  }

  // Strategy 4: cors.sh
  const corsSHResult = await tryProxy(
    `https://cors.sh/${fullUrl}`,
    'cors.sh'
  );
  if (corsSHResult) {
    this.cacheImage(cacheKey, corsSHResult);
    return { type: 'base64', data: corsSHResult };
  }

  // All failed
  console.log("❌ All strategies failed, using default avatar");
  console.log("=== STUDENT IMAGE DEBUGGING END ===");
  return this.createDefaultAvatar();
}

  isBase64String(str) {
    const base64Patterns = [
      '/9j/',
      'iVBORw0KGgoAAAANSUhEUgAA',
      'R0lGODlh',
      'UklGR',
      'Qk0'
    ];
    return base64Patterns.some(pattern => str.startsWith(pattern));
  }

  getLogoSources(logoUrl) {
    console.log("Generating logo sources for:", logoUrl);

    const baseUrl = process.env.REACT_APP_BASE_URL
      ? process.env.REACT_APP_BASE_URL.replace('/api', '').replace(/\/$/, '')
      : 'https://wsmsv1.aidigitalsec.com';

    let fullUrl;
    if (logoUrl.startsWith('http://') || logoUrl.startsWith('https://')) {
      fullUrl = logoUrl;
    } else {
      const cleanPath = logoUrl.replace(/^[~\/]+/, '');
      fullUrl = `${baseUrl}/${cleanPath}`;
    }

    return [
      { name: "direct", url: fullUrl, method: "direct" },
      {
        name: "cors-proxy-allorigins",
        url: `https://api.allorigins.win/raw?url=${encodeURIComponent(fullUrl)}`,
        method: "proxy"
      },
      {
        name: "cors-proxy-corsproxy",
        url: `https://corsproxy.io/?${encodeURIComponent(fullUrl)}`,
        method: "proxy"
      }
    ];
  }

  formatBase64ForPDF(base64String) {
    if (base64String.startsWith('data:image/')) {
      return base64String;
    }

    let mimeType = 'image/jpeg';
    if (base64String.startsWith('/9j/')) {
      mimeType = 'image/jpeg';
    } else if (base64String.startsWith('iVBORw0KGgoAAAANSUhEUgAA')) {
      mimeType = 'image/png';
    } else if (base64String.startsWith('R0lGODlh')) {
      mimeType = 'image/gif';
    } else if (base64String.startsWith('UklGR')) {
      mimeType = 'image/webp';
    }

    return `data:${mimeType};base64,${base64String}`;
  }

  async loadImageFromSource(source) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      console.log(`⏰ Timeout for ${source.name}`);
      controller.abort();
    }, 10000);

    try {
      console.log(`🔄 Fetching from ${source.name}: ${source.url}`);
      const response = await fetch(source.url, {
        signal: controller.signal,
        headers: {
          'Accept': 'image/*',
          'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36'
        },
        mode: 'cors'
      });

      clearTimeout(timeoutId);
      console.log(`📊 Response status for ${source.name}:`, response.status);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const blob = await response.blob();
      console.log(`📊 Blob info:`, { type: blob.type, size: blob.size });

      if (!blob.type.startsWith('image/')) {
        throw new Error(`Invalid content type: ${blob.type}`);
      }

      const base64Data = await this.blobToBase64(blob);
      console.log(`✅ Successfully converted to base64, length: ${base64Data.length}`);
      return base64Data;

    } catch (error) {
      clearTimeout(timeoutId);
      console.error(`❌ Error loading from ${source.name}:`, error);
      throw error;
    }
  }

  blobToBase64(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = (error) => {
        console.error("FileReader error:", error);
        reject(error);
      };
      reader.readAsDataURL(blob);
    });
  }

  getCachedImage(imagePath) {
    return this.imageCache.get(imagePath);
  }

  cacheImage(imagePath, imageData) {
    if (this.imageCache.size >= this.maxCacheSize) {
      const firstKey = this.imageCache.keys().next().value;
      this.imageCache.delete(firstKey);
    }
    this.imageCache.set(imagePath, imageData);
  }

  createDefaultAvatar() {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = 100;
    canvas.height = 100;

    ctx.fillStyle = '#e0e0e0';
    ctx.beginPath();
    ctx.arc(50, 50, 50, 0, 2 * Math.PI);
    ctx.fill();

    ctx.fillStyle = '#999';
    ctx.beginPath();
    ctx.arc(50, 35, 15, 0, 2 * Math.PI);
    ctx.fill();
    ctx.beginPath();
    ctx.arc(50, 70, 20, 0, Math.PI);
    ctx.fill();

    return {
      type: 'canvas',
      data: canvas.toDataURL('image/png', 0.8)
    };
  }

  addPlaceholderToPDF(doc, x, y, width, height) {
    doc.setFillColor(240, 240, 240);
    doc.rect(x, y, width, height, "F");
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.2);
    doc.rect(x, y, width, height);

    doc.setFontSize(6);
    doc.setTextColor(150, 150, 150);
    doc.text("Photo", x + width / 2, y + height / 2, { align: 'center' });
  }

  // ✅ UPDATED: Uses schoolDetails from API instead of sessionStorage
addStudentContent(doc, studentData, schoolDetails) {
  const navy = [11, 31, 61];
  const orange = [237, 158, 15];
  const leftMargin = 6;
  const lineEndX = 98;

  const drawField = (label, value, x, y, lineEnd) => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(...navy);
    doc.text(label, x, y);
    const labelWidth = doc.getTextWidth(label);
    const textStartX = x + labelWidth + 2;

    doc.setDrawColor(...orange);
    doc.setLineWidth(0.5);
    doc.line(textStartX, y + 0.8, lineEnd, y + 0.8);

    if (value) {
      doc.setFont("helvetica", "normal");
      doc.text(String(value), textStartX + 1, y);
    }
  };

  let age = "";
  if (studentData.dob) {
    const [d, m, y] = studentData.dob.split("/").map(Number);
    if (d && m && y) {
      const dob = new Date(y, m - 1, d);
      age = Math.floor((new Date() - dob) / (365.25 * 24 * 3600 * 1000));
    }
  }

 let y = 26;        
const rowGap = 8.3; 
  drawField("NAME:-", studentData.studentName?.trim(), leftMargin, y, lineEndX);
  y += rowGap;

  drawField("PTS CLASS:-", studentData.ptsClass, leftMargin, y, 52);
  drawField("SCHOOL CLASS:-", studentData.class, 58, y, lineEndX);
  y += rowGap;

  drawField("AGE:-", age, leftMargin, y, 28);
  drawField("DATE OF JOINING:-", studentData.dateofAdmission, 34, y, lineEndX);
  y += rowGap;

  drawField("PARENT'S NAME:-", studentData.guardianName, leftMargin, y, lineEndX);
  y += rowGap;

  drawField("PARENT'S NUMBER:-", studentData.fatherMobile, leftMargin, y, lineEndX);
  y += rowGap;

  drawField("DATE OF BIRTH:-", studentData.dob, leftMargin, y, lineEndX);
  y += rowGap;

  drawField("ROLL NUMBER:-", studentData.rollNo || "N/A", leftMargin, y, lineEndX);
  // y here lands at 33 + 7.5*6 = 78, leaving a 7mm gap before footer at 85 — no collision

  const pageWidth = 140, pageHeight = 95, footerH = 10;
  doc.setFillColor(...orange);
  doc.rect(0, pageHeight - footerH, pageWidth, footerH, "F");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(...navy);
  const footerY = pageHeight - footerH / 2 + 1.5;

  doc.text((schoolDetails.website || "PEHCHAANSTREETSCHOOL.ORG").toUpperCase(), 6, footerY);
  doc.text(schoolDetails.phoneNo || "", pageWidth / 2, footerY, { align: "center" });
  doc.text("AUTH SIGN.", pageWidth - 6, footerY, { align: "right" });
}

  generateAndOpenPDF(doc) {
    const pdfBlob = doc.output("blob");
    const pdfUrl = URL.createObjectURL(pdfBlob);
    window.open(pdfUrl, "_blank");
    setTimeout(() => URL.revokeObjectURL(pdfUrl), 5000);
  }

  showLoadingIndicator() {
    const loader = document.getElementById('pdf-loader') || this.createLoader();
    loader.style.display = 'block';
  }

  hideLoadingIndicator() {
    const loader = document.getElementById('pdf-loader');
    if (loader) loader.style.display = 'none';
  }

  createLoader() {
    const loader = document.createElement('div');
    loader.id = 'pdf-loader';
    loader.innerHTML = `
      <div style="position: fixed; top: 0; left: 0; width: 100%; height: 100%; 
                  background: rgba(0,0,0,0.5); display: flex; align-items: center; 
                  justify-content: center; z-index: 9999;">
        <div style="background: white; padding: 20px; border-radius: 8px; text-align: center;">
          <div style="width: 40px; height: 40px; border: 4px solid #f3f3f3; 
                      border-top: 4px solid #3498db; border-radius: 50%; 
                      animation: spin 1s linear infinite; margin: 0 auto 15px;"></div>
          <p>Generating ID Card...</p>
        </div>
      </div>
    `;

    const style = document.createElement('style');
    style.textContent = `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(style);
    document.body.appendChild(loader);
    return loader;
  }

  showErrorMessage(message) {
    const errorDiv = document.createElement('div');
    errorDiv.innerHTML = `
      <div style="position: fixed; top: 20px; right: 20px; background: #f44336; 
                  color: white; padding: 16px; border-radius: 4px; z-index: 10000;
                  box-shadow: 0 4px 6px rgba(0,0,0,0.1); max-width: 400px;">
        <div style="font-weight: bold; margin-bottom: 8px;">PDF Generation Error</div>
        <div style="font-size: 14px;">${message}</div>
        <button onclick="this.parentElement.remove()" 
                style="position: absolute; top: 8px; right: 8px; 
                       background: none; border: none; color: white; 
                       cursor: pointer; font-size: 18px;">&times;</button>
      </div>
    `;
    document.body.appendChild(errorDiv);
    setTimeout(() => {
      if (errorDiv.parentElement) errorDiv.remove();
    }, 5000);
  }
}

 // Initialize PDF Manager
  const pdfManager = new ImprovedPDFImageManager();

const Admission = () => {
  const [totalRecords, setTotalRecords] = useState(0);
const [isSearching, setIsSearching] = useState(false);
const searchDebounceRef = useRef(null);
  const [isPrintingAdmission, setIsPrintingAdmission] = useState(false);
  const [isMaster, setIsMaster] = useState(false);
const [branches, setBranches] = useState([]);
const [selectedBranchId, setSelectedBranchId] = useState(1);

  const [isPreloadingImages, setIsPreloadingImages] = useState(false);
  const [canAdd, setCanAdd] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [canPrint, setCanPrint] = useState(false);

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [admissionData, setAdmissionData] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(30);
  const [open, setOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedAdmission, setSelectedAdmission] = useState(null);

  const decodeFormId = (encodedFormId) => {
    if (!encodedFormId || typeof encodedFormId !== "string") {
      console.error("encodedFormId is missing or invalid:", encodedFormId);
      return null;
    }
    try {
      const paddedEncodedFormId = encodedFormId.padEnd(Math.ceil(encodedFormId.length / 4) * 4, "=");
      const bytes = base64.toByteArray(paddedEncodedFormId);
      return new TextDecoder().decode(bytes);
    } catch (error) {
      console.error("Error decoding formId:", error);
      return null;
    }
  };

  const fetchBranches = async () => {
  try {
    const apiUrl = process.env.REACT_APP_BASE_URL;
    const token = sessionStorage.getItem("token");
    const response = await fetch(`${apiUrl}/Branch/GetBranch`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: token,
      },
      body: JSON.stringify({}),
    });

    if (response.ok) {
      const data = await response.json();
      setBranches(data);
    } else {
      console.error("Failed to fetch branches");
    }
  } catch (error) {
    console.error("Error fetching branches:", error);
  }
};


  const { encodedFormId } = useParams();
  const formId = decodeFormId(encodedFormId);

  const Authorizer = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const response = await fetch(`${apiUrl}/CPanel/Module_Authorizer`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({ formId, }),
      });

      const result = await response.json();
      const data = result[0];

      setCanEdit(data?.uModify === 1);
      setCanAdd(data?.uWrite === 1);
      setCanPrint(data?.uPrint === 1);
      setCanDelete(data?.uDelete === 1);
    } catch (error) {
      console.error("Authorization check failed:", error);
    }
  };

  let navigate = useNavigate();

 const handleChangeRowsPerPage = (event) => {
  const newSize = parseInt(event.target.value, 10);
  setRowsPerPage(newSize);
  setPage(0);
  searchAdmission(searchTerm, 0, newSize);
};

  const handleOpen = () => {
    setOpen(true);
  };

  const handleOpenModal = () => {
    setOpenModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
  }

  const handleClose = () => {
    setOpen(false);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleFileChange = (event) => {
    setSelectedFile(event.target.files[0]);
  };

const handleSearch = (event) => {
  const value = event.target.value;
  setSearchTerm(value);
  setPage(0);

  if (searchDebounceRef.current) {
    clearTimeout(searchDebounceRef.current);
  }
  searchDebounceRef.current = setTimeout(() => {
    searchAdmission(value, 0, rowsPerPage);
  }, 400);
};

  const handleViewDocuments = (student) => {
    setSelectedStudent(student);
    setOpen(true);
  };

  const fetchStudentAdmission = async (studentId) => {
    try {
       setIsPrintingAdmission(true); 
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem('token');
      const response = await fetch(`${apiUrl}/Admission/rptAdmission`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({
          studentId: studentId,
        }),
      });

      if (!response.ok) {
        throw new Error(`Error fetching admission data: ${response.status}`);
      }

    const data = await response.json();
const studentData = {
  ...data[0],
  studentImagePath: data[0].profileImage || data[0].studentProfileLogo || data[0].studentImagePath
};
generateAdmissionFormPDFInNewTab(studentData);
    } catch (error) {
      console.error(error);
      setIsPrintingAdmission(false);
    }
  };

const generateAdmissionFormPDFInNewTab = async (studentData) => {
  try {
    const doc = new jsPDF(); // A4 portrait, 210x297mm

    // Fetch school details
    let schoolName = "N/A";
    let logoDataUrl = null;

    try {
      const token = sessionStorage.getItem("token");
      if (token) {
        const response = await fetch('https://wsmsv1.aidigitalsec.com/api/FeeReport/GetSchoolName', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': token },
          body: JSON.stringify({}),
        });
        if (response.ok) {
          const apiData = await response.json();
          schoolName = apiData[0]?.schoolName?.replace(/['"]+/g, "") || "N/A";
          const logoImg = apiData?.[0]?.logoImg;
          if (logoImg && typeof logoImg === 'string' && logoImg.trim() !== '') {
            try {
              let cleanedBase64 = logoImg
                .replace(/^data:image\/[a-zA-Z]+;base64,/, '')
                .replace(/\s/g, '')
                .replace(/\n/g, '');
              const paddingLength = (4 - (cleanedBase64.length % 4)) % 4;
              cleanedBase64 += '='.repeat(paddingLength);
              if (/^[A-Za-z0-9+/=]+$/.test(cleanedBase64)) {
                logoDataUrl = `data:image/png;base64,${cleanedBase64}`;
              }
            } catch (e) {
              console.warn("Logo processing failed:", e.message);
            }
          }
        }
      }
    } catch (error) {
      console.error("Error fetching school details:", error);
    }

    const navy = [11, 31, 61];

    // Logo top-left
    if (logoDataUrl) {
      try {
        doc.addImage(logoDataUrl, 'PNG', 26, 30, 30, 20);
      } catch (e) {
        console.warn("Could not add logo:", e.message);
      }
    }

    // Title
    doc.setFont("helvetica", "bold");
    doc.setFontSize(28);
    doc.setTextColor(20, 20, 20);
    doc.text("Admission Form", 71, 40);

    // Subtitle — letter-spaced
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.setTextColor(60, 60, 60);
    doc.text(schoolName.toUpperCase(), 71, 48, { charSpace: 1.2 });

    // Section header
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    doc.text("Personal Information", 21.6, 72.5);

    // Age calc
    let age = "";
    if (studentData.dob) {
      const [d, m, y] = studentData.dob.split("/").map(Number);
      if (d && m && y) {
        const dob = new Date(y, m - 1, d);
        age = Math.floor((new Date() - dob) / (365.25 * 24 * 3600 * 1000));
      }
    }

    const fields = [
      { label: "Student Name", value: studentData.studentName },
      { label: "Student Age", value: age },
      { label: "Roll Number", value: studentData.rollNo || "N/A" },
      { label: "Gender", value: studentData.gender },
      { label: "School Class", value: studentData.class },
      { label: "Father Name", value: studentData.guardianName },
      { label: "Mother Name", value: studentData.mothersName },
      { label: "Student Aadhar No.", value: studentData.adharNo },
      { label: "Parents Aadhar No.", value: studentData.parentAdharNo || studentData.adharNo },
      { label: "School Name", value: schoolName },
      { label: "Phone Number", value: studentData.fatherMobile },
    ];

    const boxX = 59, boxW = 136, rowH = 8.8, rowPitch = 12.6;
    let y = 79.0;

    doc.setFontSize(10);
    fields.forEach(({ label, value }) => {
      doc.setFont("helvetica", "normal");
      doc.setTextColor(60, 60, 60);
      doc.text(`${label} :`, 56, y + rowH / 2 + 1, { align: "right" });

      doc.setFillColor(245, 245, 245);
      doc.rect(boxX, y, boxW, rowH, "F");
      doc.setTextColor(0, 0, 0);
      doc.text(String(value || ""), boxX + 3, y + rowH / 2 + 1);

      y += rowPitch;
    });

    // Address (taller box)
    doc.setFont("helvetica", "normal");
    doc.setTextColor(60, 60, 60);
    doc.text("Address :", 56, y + 12, { align: "right" });
    doc.setFillColor(245, 245, 245);
    doc.rect(boxX, y, boxW, 23.4, "F");
    doc.setTextColor(0, 0, 0);
    const addrLines = doc.splitTextToSize(studentData.permanentAddress || "", boxW - 6);
    doc.text(addrLines, boxX + 3, y + 7);
    y += 23.4;

    // Divider 1
    doc.setDrawColor(180, 180, 180);
    doc.setLineWidth(0.2);
    doc.line(25.5, y + 6.7, 195, y + 6.7);

    // Type Of Student header
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    doc.text("Type Of Student", 26, y + 17);

    // Checkboxes row
    const checkY = y + 27;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);

    const drawCheckbox = (label, labelX, boxXpos, checked) => {
      doc.text(label, labelX, checkY);
      doc.setDrawColor(0, 0, 0);
      doc.rect(boxXpos, checkY - 3.5, 3.5, 3.5);
      if (checked) doc.text("X", boxXpos + 0.6, checkY - 0.6);
    };

    const studentType = studentData.studentType || "";
    drawCheckbox("Regular (6 month)", 26, 62, studentType === "Regular");
    drawCheckbox("New (1 month / 2 Week)", 80, 132, studentType === "New");
    drawCheckbox("Irregular", 150, 172, studentType === "Irregular");

    // Divider 2
    doc.line(25.5, checkY + 10, 195, checkY + 10);

    // Open PDF
    const pdfBlob = doc.output("blob");
    const pdfUrl = URL.createObjectURL(pdfBlob);
    window.open(pdfUrl, "_blank");
    setTimeout(() => URL.revokeObjectURL(pdfUrl), 5000);
    setIsPrintingAdmission(false);

  } catch (error) {
    console.error("Error generating PDF:", error.message);
    setIsPrintingAdmission(false);
  }
};


 

  // Updated fetchStudentId function with better error handling
  const fetchStudentId = async (studentId) => {
    try {
      console.log("🔄 Fetching student data for ID:", studentId);
      
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem('token');
      
      if (!token) {
        throw new Error("Authentication token not found");
      }

      const response = await fetch(`${apiUrl}/Admission/rptAdmission`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({
          studentId: studentId,
        }),
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log("📊 API response for student data:", data);

      if (!data || !data[0]) {
        throw new Error("No student data returned from API");
      }

      // Map studentProfileLogo to studentImagePath for consistency
      const studentData = {
        ...data[0],
        studentImagePath: data[0].studentProfileLogo || data[0].studentImagePath
      };

      console.log("✅ Student data prepared, generating ID card...");
      await pdfManager.generateIdCardPDF(studentData);
      
    } catch (error) {
      console.error("❌ Error fetching student data:", error);
      pdfManager.showErrorMessage(`Failed to fetch student data: ${error.message}`);
    }
  };

  const preloadStudentImages = async (studentsArray) => {
    setIsPreloadingImages(true);
    try {
      const promises = studentsArray.map(async (student) => {
        const studentData = { ...student, studentImagePath: student.profileImage || student.studentProfileLogo };
        return await pdfManager.handleStudentImage(studentData);
      });
      await Promise.all(promises);
    } finally {
      setIsPreloadingImages(false);
    }
  };

  const handleDelete = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem('token');

      const response = await fetch(`${apiUrl}/Admission/Id?Id=${selectedAdmission.studentId}`, {
        method: 'DELETE',
        headers: {
          Authorization: token,
        },
      });
      if (response.ok) {
        setSnackbarMessage('Admission deleted successfully!');
        setOpenSnackbar(true);
      }
      if (!response.ok) {
        throw new Error(`Error deleting admission: ${response.status}`);
      }

      searchAdmission(searchTerm, page, rowsPerPage)
      setShowDeleteModal(false);
    } catch (error) {
      console.error(error);
    }
  };

  const handleUpload = async () => {
    const apiUrl = process.env.REACT_APP_BASE_URL;
    const token = sessionStorage.getItem('token');
    const formData = new FormData();
    formData.append("file", selectedFile);

    try {
      const response = await fetch(`${apiUrl}/Admission/importAdmission`, {
        method: 'POST',
        headers: {
          Authorization: token,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Error uploading file: ${response.status}`);
      }
      // alert('Data imported successfully')
       setSnackbarMessage('Data imported successfully');  
       setOpenSnackbar(true); 

      handleClose();
    } catch (error) {
      console.error(error);
    }
  };

  const handleClick = () => {
    navigate("/addadmission");
  };

 const searchAdmission = async (searchText = "", pageNo = 0, pageSize = rowsPerPage) => {
  try {
    setIsSearching(true);
    const apiUrl = process.env.REACT_APP_BASE_URL;
    const token = sessionStorage.getItem("token");
    const branchId = isMaster ? selectedBranchId : sessionStorage.getItem("branchId");

    const params = new URLSearchParams({
      Branch_Id: branchId || "",
      PageNo: pageNo + 1,       // API is 1-indexed
      PageSize: pageSize,
      SearchText: searchText || "",
      ClassId: 0,
      SectionId: 0,
      StudentId: 0,
    });

    const response = await fetch(`${apiUrl}/Admission/SearchAdmission?${params.toString()}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: token,
      },
      body: JSON.stringify({}),
    });

    if (!response.ok) {
      throw new Error(`Error searching admission data: ${response.status}`);
    }

    const data = await response.json();
    const records = Array.isArray(data) ? data : data.data || [];
    const total = data.totalRecords ?? data.totalCount ?? records.length;

    setAdmissionData(records);
    setTotalRecords(total);
    return records;
  } catch (error) {
    console.error(error);
    setAdmissionData([]);
    setTotalRecords(0);
  } finally {
    setIsSearching(false);
  }
};


useEffect(() => {
  const initialize = async () => {
    const isMasterUser = sessionStorage.getItem("is_Master") === "true";
    setIsMaster(isMasterUser);

    if (isMasterUser) {
      fetchBranches();
    } else {
      const branchId = sessionStorage.getItem("branchId");
      setSelectedBranchId(branchId);
    }

    const [admissionDataResult] = await Promise.all([
      searchAdmission(searchTerm, page, rowsPerPage),
      Authorizer()
    ]);

   
  };
  initialize();
}, []);



  useEffect(() => {
  const isMasterUser = sessionStorage.getItem("is_Master") === "true";
  setIsMaster(isMasterUser);

  if (isMasterUser) {
    fetchBranches();
  } else {
    const branchId = sessionStorage.getItem("branchId");
    setSelectedBranchId(branchId);
  }

  Authorizer();
}, []);


useEffect(() => {
  if (selectedBranchId) {
    searchAdmission(searchTerm, page, rowsPerPage);
  }
}, [selectedBranchId]);



  const filteredData = admissionData.filter((year) => {
    const studentNameMatch = year.studentName
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    const classNameMatch = year.className
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    return studentNameMatch || classNameMatch;
  });

 const displayedData = admissionData;

const handleChangePage = (newPage) => {
  setPage(newPage);
  searchAdmission(searchTerm, newPage, rowsPerPage);
};
  const handleDownload = async () => {
    const apiUrl = process.env.REACT_APP_BASE_URL;
    const token = sessionStorage.getItem('token');
    const branchId = isMaster ? selectedBranchId : sessionStorage.getItem("branchId");

    try {
      const response = await fetch(`${apiUrl}/Admission/DownloadExcel_AdmissionReport?Branch_Id=${branchId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        throw new Error(`Error downloading file: ${response.status}`);
      }

      // Get current date and time
      const now = new Date();
      const formattedDate = now.toISOString().split('T')[0];
      const formattedTime = now.toTimeString().split(' ')[0].replace(/:/g, '-');
      const filename = `AdmissionReport_${formattedDate}_${formattedTime}.csv`;

      // Convert response to blob
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
    }
  };

  // Function to handle opening of image or PDF in new tab
  const handleViewDocument = (path) => {
    window.open(`${process.env.REACT_APP_DOCUMENT_URL}/${String(path).replace(/^\/+/, '')}`, "_blank");
  };

  const handleDownloadSample = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const response = await fetch(`${apiUrl}/Admission/downloadFormatImportAdmission`);
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = 'Sample_Import_Admission.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading the file:', error);
    }
  };


  return (
    <Container
  sx={{
    width: '100%',
    maxWidth: '100% !important',
    padding: 0,
  }}
>
      <AppBar
  position="static"
  sx={{
    backgroundColor: "#0B1F3D",
    boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
  }}
>
  <Toolbar sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 3 }}>
    
    {/* Left: Title */}
    <Typography
      variant="h4"
      sx={{
        fontWeight: 700,
        letterSpacing: "0.5px",
        color: "#ffffff",
      }}
    >
      Admission Report
    </Typography>

    {/* Right: Branch Dropdown */}
   {isMaster && (
  <Box sx={{ position: "relative" }}>
    <FormControl size="small" sx={{ width: 220 }}>
      <InputLabel
        sx={{
          color: "rgba(255,255,255,0.6)",
          fontSize: "0.75rem",
          fontWeight: 500,
          letterSpacing: "0.6px",
          textTransform: "uppercase",
          top: "-2px",
          "&.Mui-focused": { color: "#64B5F6" },
          "&.MuiFormLabel-filled": { color: "rgba(255,255,255,0.7)" },
        }}
      >
        Branch
      </InputLabel>
      <Select
        value={selectedBranchId || ""}
        onChange={(e) => setSelectedBranchId(e.target.value)}
        label="Branch"
        startAdornment={
          <InputAdornment position="start">
            <LocationOnIcon sx={{ fontSize: 16, color: "rgba(255,255,255,0.5)", ml: 0.5 }} />
          </InputAdornment>
        }
        sx={{
          color: "#ffffff",
          fontWeight: 500,
          fontSize: "0.875rem",
          letterSpacing: "0.3px",
          backgroundColor: "rgba(255,255,255,0.07)",
          backdropFilter: "blur(8px)",
          borderRadius: "8px",
          transition: "background-color 0.2s ease, box-shadow 0.2s ease",

          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "rgba(255,255,255,0.15)",
            borderWidth: "1px",
            transition: "border-color 0.2s ease",
          },
          "&:hover": {
            backgroundColor: "rgba(255,255,255,0.11)",
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "rgba(255,255,255,0.35)",
            },
          },
          "&.Mui-focused": {
            backgroundColor: "rgba(255,255,255,0.1)",
            boxShadow: "0 0 0 3px rgba(100,181,246,0.18)",
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "#64B5F6",
              borderWidth: "1.5px",
            },
          },
          "& .MuiSelect-icon": {
            color: "rgba(255,255,255,0.5)",
            transition: "color 0.2s ease",
          },
          "&.Mui-focused .MuiSelect-icon": {
            color: "#64B5F6",
          },
          "& .MuiSelect-select": {
            py: 1.1,
          },
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              mt: 0.5,
              borderRadius: "10px",
              border: "1px solid rgba(11,31,61,0.12)",
              boxShadow: "0 8px 32px rgba(11,31,61,0.18), 0 2px 8px rgba(0,0,0,0.08)",
              overflow: "hidden",
              "& .MuiList-root": { py: 0.5 },
            },
          },
        }}
      >
        {branches.map((branch) => (
          <MenuItem
            key={branch.branchId}
            value={branch.branchId}
            sx={{
              fontSize: "0.875rem",
              fontWeight: 500,
              color: "#0B1F3D",
              px: 2,
              py: 1.1,
              mx: 0.5,
              borderRadius: "6px",
              transition: "background-color 0.15s ease",
              "&:hover": {
                backgroundColor: "rgba(11,31,61,0.07)",
              },
              "&.Mui-selected": {
                backgroundColor: "rgba(25,118,210,0.1)",
                color: "#1565C0",
                fontWeight: 600,
                "&:hover": {
                  backgroundColor: "rgba(25,118,210,0.15)",
                },
              },
            }}  
          >
            {branch.branchName}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  </Box>
)}
  </Toolbar>
</AppBar>
      <TextField
        fullWidth
        variant="outlined"
        margin="normal"
        placeholder="Search by student name or class"
        value={searchTerm}
        onChange={handleSearch}
      />

      <Button
        variant="outlined"
        sx={{
          mt: 2,
          mx: 1,
          backgroundColor: "#2196f3",
          color: "#ffffff",
          "&:hover": {
            backgroundColor: "#2196f3", // Set the same color as the background to remove hover effect
          },
        }}
        onClick={handleClick}
      >
        NEW ADMISSION
      </Button>
      <Button
        variant="outlined"
        sx={{
          mt: 2,
          mx: 1,
          backgroundColor: "#f50057",
          color: "#ffffff",
          "&:hover": {
            backgroundColor: "#f50057",
          },
        }}
        onClick={handleOpenModal} // Open the modal
      >
        IMPORT
      </Button>

      <Button
        variant="outlined"
        sx={{
          mt: 2,
          mx: 1,
          backgroundColor: "#3f51b5",
          color: "#ffffff",
          "&:hover": {
            backgroundColor: "#3f51b5",
          },
        }}
        onClick={handleDownload} // Call the handleDownload function
      >
        <CloudDownload sx={{ mx: 1 }} />
        DOWNLOAD
      </Button>
       <Tooltip title={!canAdd && "You are not Authorized to Print"} arrow><span>
     <Button
  variant="outlined"
  sx={{
    mt: 2,
    mx: 1,
    backgroundColor: canPrint ? "#4caf50" : "#cccccc", // Different background color when disabled
    color: canPrint ? "#ffffff" : "#666666", // Different text color when disabled
    borderColor: canPrint ? "#4caf50" : "#cccccc", // Different border color when disabled
    "&:hover": {
      backgroundColor: canPrint ? "#45a049" : "#cccccc", // Hover effect only when enabled
    },
    "&.Mui-disabled": {
      backgroundColor: "#cccccc", // Background color when disabled
      color: "#666666", // Text color when disabled
      borderColor: "#cccccc", // Border color when disabled
    },
  }}
  onClick={() => navigate("/bulk-id-card")}
  disabled={!canPrint}
>
  GENERATE BULK ID CARD
</Button></span></Tooltip>



    <TableContainer component={Paper} sx={{ mt: 3, minWidth: "100%" }}>
  <Table
    sx={{
      minWidth: "100%",
      border: '1px solid #ccc',
      '& .MuiTableCell-root': {
        borderRight: '1px solid #ccc',
        fontSize: '1rem',
      },
    }}
    aria-label="admission table"
  >
    <TableHead>
      <TableRow>
        <TableCell sx={{ borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem', backgroundColor: '#f5f5f5' }}>
  S.No
</TableCell>
        <TableCell sx={{ borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem', backgroundColor: '#f5f5f5' }}>
          Admission No
        </TableCell>
        <TableCell sx={{ borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem', backgroundColor: '#f5f5f5' }}>
          Student Name
        </TableCell>
        <TableCell sx={{ borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem', backgroundColor: '#f5f5f5' }}>
          Class
        </TableCell>
        <TableCell sx={{ borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem', backgroundColor: '#f5f5f5' }}>
          Mobile No
        </TableCell>
        {/* Documents column — commented out */}
        {/* <TableCell sx={{ borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem', backgroundColor: '#f5f5f5' , textAlign:'center'}}>
          Documents
        </TableCell> */}
        <TableCell sx={{ borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem', backgroundColor: '#f5f5f5' , textAlign:'center'}}>
          Admission Form
        </TableCell>
        <TableCell sx={{ borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem', backgroundColor: '#f5f5f5' , textAlign:'center'}}>
          Identity Card
        </TableCell>
        <TableCell sx={{ borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem', backgroundColor: '#f5f5f5', textAlign: 'center' }}>
          Action
        </TableCell>
      </TableRow>
    </TableHead>
    <TableBody>

       {isSearching ? (
    <TableRow>
      <TableCell colSpan={8} sx={{ textAlign: 'center', py: 6 }}>
        <div style={{
          width: '36px', height: '36px', border: '4px solid #f3f3f3',
          borderTop: '4px solid #3498db', borderRadius: '50%',
          animation: 'spin 1s linear infinite', margin: '0 auto 12px',
        }} />
        <Typography>Loading...</Typography>
      </TableCell>
    </TableRow>
  ) : (
      
      displayedData.map((year, index) => (
        <TableRow key={year.admissionNo} sx={{ borderTop: '1px solid #ccc' }}>
           <TableCell sx={{ fontSize: '1.1rem' }}>{page * rowsPerPage + index + 1}</TableCell>
          <TableCell sx={{ fontSize: '1.1rem' }}>{year.admissionNo}</TableCell>
          <TableCell sx={{ fontSize: '1.1rem' }}>{year.studentName}</TableCell>
          <TableCell sx={{ fontSize: '1.1rem' }}>{year.className}</TableCell>
          <TableCell sx={{ fontSize: '1.1rem' }}>{year.mobileNo}</TableCell>
          {/* Documents View button — commented out */}
          {/* <TableCell sx={{ fontSize: '1.1rem', textAlign:'center' }}>
            <Button
              variant="contained"
              onClick={() => handleViewDocuments(year)}
              sx={{ marginRight: 1 }}
            >
              View
            </Button>
          </TableCell> */}
          <TableCell sx={{ fontSize: '1.1rem' , textAlign:'center'}}>
             <Tooltip title={!canPrint && "You are not Authorized to Print"} arrow><span>
            <IconButton size="small" disabled={!canPrint}>
              <Print onClick={() => fetchStudentAdmission(year.studentId)} />
            </IconButton></span></Tooltip>
          </TableCell>
          <TableCell sx={{ fontSize: '1.1rem' , textAlign:'center'}}>
                         <Tooltip title={!canPrint && "You are not Authorized to Print"} arrow><span>

            <IconButton onClick={async () => await fetchStudentId(year.studentId)} disabled={!canPrint}>
              <IdCard />
            </IconButton></span></Tooltip>
          </TableCell>
          <TableCell sx={{ fontSize: '1.1rem', textAlign: 'center' }}>
             <Tooltip title={!canEdit && "You are not Authorized to Edit"} arrow><span>
           <Button
  variant="contained"
  color="warning"
  onClick={() => navigate(`/updateadmission/${year.studentId}?Branch_Id=${isMaster ? selectedBranchId : sessionStorage.getItem("branchId")}`)}
  sx={{ marginRight: 1 }}
  startIcon={<Edit />}
  disabled={!canEdit}
>
  Edit
</Button>
</span></Tooltip>
             <Tooltip title={!canDelete && "You are not Authorized to Delete"} arrow><span>

            <Button
              variant="contained"
              color="error"
              onClick={() => {
                setSelectedAdmission(year);
                setShowDeleteModal(true);
              }}
              sx={{ marginRight: 1 }}
              startIcon={<Delete />}
              disabled={!canDelete}
            >
              Delete
            </Button></span></Tooltip>
          </TableCell>
        </TableRow>
      ))
     ) }</TableBody>
  </Table>
</TableContainer>

      {!searchTerm && (
        
       <div style={{ display: "flex", justifyContent: "center", alignItems: "center", marginTop: "16px" }}>
        <FormControl size="small" sx={{ minWidth: 100, mx: 2 }}>
  <Select value={rowsPerPage} onChange={handleChangeRowsPerPage} disabled={isSearching}>
    <MenuItem value={10}>10 / page</MenuItem>
    <MenuItem value={30}>30 / page</MenuItem>
    <MenuItem value={50}>50 / page</MenuItem>
    <MenuItem value={100}>100 / page</MenuItem>
  </Select>
</FormControl>
  <IconButton
    onClick={() => handleChangePage(page - 1)}
    disabled={page === 0 || isSearching}
    sx={{ color: page === 0 ? "grey" : "#2196f3" }}
  >
    <NavigateBefore />
  </IconButton>
  <span style={{ margin: "0 8px", fontSize: "16px" }}>{page + 1}</span>
  <IconButton
    onClick={() => handleChangePage(page + 1)}
    disabled={page + 1 >= Math.ceil(totalRecords / rowsPerPage) || isSearching}
    sx={{ color: page + 1 >= Math.ceil(totalRecords / rowsPerPage) ? "grey" : "#2196f3" }}
  >
    <NavigateNext />
  </IconButton>
</div>
      )}


      {/* Modal for viewing documents */}
     <Modal
  open={open}
  onClose={handleClose}
  aria-labelledby="documents-modal-title"
  aria-describedby="documents-modal-description"
>
  <Box
    sx={{
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: 500,
      bgcolor: 'background.paper',
      borderRadius: 2,
      boxShadow: 24,
      overflow: 'hidden',
    }}
  >
    {/* Header */}
    <Box sx={{ backgroundColor: '#0B1F40', padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Typography id="documents-modal-title" variant="h6" sx={{ color: '#fff' }}>
        View Documents
      </Typography>
   <IconButton
  edge="end"
  color="inherit"
  onClick={handleClose}
  aria-label="close"
  sx={{
    color: '#fff', // This ensures the icon color is white
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)', // Optional: Add a slight background color on hover for better visibility
    },
  }}
>
  <CloseIcon />
</IconButton>

    </Box>
    {/* Content */}
    <Box sx={{ padding: '20px' }}>
      {selectedStudent && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          <Button
            variant="outlined"
            onClick={() => handleViewDocument(selectedStudent.aadharPathFront)}
            disabled={!selectedStudent.aadharPathFront}
          >
            Aadhar Front
          </Button>
          <Button
            variant="outlined"
            onClick={() => handleViewDocument(selectedStudent.adharPathBack)}
            disabled={!selectedStudent.adharPathBack}
          >
            Aadhar Back
          </Button>
          <Button
            variant="outlined"
            onClick={() => handleViewDocument(selectedStudent.birthDocumentPath)}
            disabled={!selectedStudent.birthDocumentPath}
          >
            Birth Certificate
          </Button>
          <Button
            variant="outlined"
            onClick={() => handleViewDocument(selectedStudent.transferDocumentPath)}
            disabled={!selectedStudent.transferDocumentPath}
          >
            Transfer Certificate
          </Button>
          <Button
            variant="outlined"
            onClick={() => handleViewDocument(selectedStudent.otherDocumentPath)}
            disabled={!selectedStudent.otherDocumentPath}
          >
            Others
          </Button>
        </Box>
      )}
    </Box>
  </Box>
</Modal>


      {/* Modal for file upload */}
      <Modal
        open={openModal}
        onClose={handleCloseModal}
        aria-labelledby="upload-modal-title"
        aria-describedby="upload-modal-description"
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={openModal}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "60%",
              bgcolor: "background.paper",
              borderRadius: 1,
              boxShadow: 24,
              p: 4,
            }}
          >
            <AppBar
              position="static"
              style={{ backgroundColor: "#0B1F3D", marginBottom: "20px" }}
            >
              <Toolbar>
                <Typography variant="h4" component="div">
                  Upload Excel File
                </Typography>
              </Toolbar>
            </AppBar>
            <Typography
              id="upload-modal-title"
              variant="h6"
              component="div"
              gutterBottom
            >
              Tips for Uploading File
            </Typography>
            <ul
              style={{
                marginBottom: "20px",
                paddingLeft: "20px",
                listStyleType: "disc",
              }}
            >
              <li>Ensure the file is in .xlsx format.</li>
              <li>Make sure all required fields are filled out.</li>
              <li>Check for any duplicate entries.</li>
              <li>Remove any special characters from the data.</li>
              <li>
                Ensure proper column headers are used as per the template.
              </li>
            </ul>
            <input
              type="file"
              // accept=".xlsx, .xls"
              onChange={handleFileChange}
              style={{ marginBottom: "20px", display: "block" }}
            />
            <Box display="flex" justifyContent="space-between">
              <Button
                variant="contained"
                onClick={handleUpload}
                color="primary"
              >
                Upload
              </Button>
              <Button
                variant="contained"
                onClick={handleDownloadSample}
                color="warning"
              >
                Download Sample Report
              </Button>
            </Box>
          </Box>
        </Fade>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal open={showDeleteModal} onClose={handleCloseDeleteModal} aria-labelledby="delete-modal-title" aria-describedby="delete-modal-description">
  <Box
    sx={{
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: 400,
      bgcolor: 'background.paper',
      borderRadius: 2,
      boxShadow: 24,
      overflow: 'hidden',
    }}
  >
    {/* Header */}
    <Box sx={{ backgroundColor: '#0B1F40', padding: '12px 16px' }}>
      <Typography id="delete-modal-title" variant="h6" sx={{ color: '#fff' }}>
        Delete Confirmation ⚠️
      </Typography>
    </Box>

    {/* Content */}
    <Box sx={{ padding: '20px' }}>
      <Typography id="delete-modal-description" sx={{ mb: 2 }}>
        Are you sure you want to delete this Admission?
      </Typography>

      {/* Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
        <Button onClick={handleCloseDeleteModal} variant="outlined" sx={{ mr: 1 }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleDelete}
        >
          Yes, Delete
        </Button>
      </Box>
    </Box>
  </Box>
</Modal>

<Snackbar
  open={openSnackbar}
  autoHideDuration={4000}
  onClose={() => setOpenSnackbar(false)}
  anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
>
  <Alert onClose={() => setOpenSnackbar(false)} severity="success" sx={{ width: '100%' }}>
    {snackbarMessage}
  </Alert>
</Snackbar>

{isPrintingAdmission && (
  <Box
    sx={{
      position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
      background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center',
      justifyContent: 'center', zIndex: 9999,
    }}
  >
    <Box sx={{ background: 'white', padding: '20px', borderRadius: '8px', textAlign: 'center' }}>
      <div style={{
        width: '40px', height: '40px', border: '4px solid #f3f3f3',
        borderTop: '4px solid #3498db', borderRadius: '50%',
        animation: 'spin 1s linear infinite', margin: '0 auto 15px',
      }} />
      <Typography>Generating Admission Form...</Typography>
    </Box>
  </Box>
)}
    </Container>
  );
};

export default Admission;